/**
 * The text of a system notification, and how much of it a stranger gets to write.
 *
 * Pure and separate from the hook, because every decision here is about untrusted
 * input rather than about React. A notification's words come from a display name
 * anyone can set and a note body anyone can publish, and unlike a row in the app
 * this text is rendered by the *operating system* — outside the page, outside the
 * CSP, in a surface the user reads as coming from Setu itself.
 *
 * So three rules, all asserted in `desktopNotice.test.ts`:
 *
 * 1. **No markup and no layout.** `<`, `>` and `&` are replaced and all whitespace
 *    collapses to single spaces. A Linux notification daemon parses a small HTML
 *    subset in the body, so a display name of `<a href="…">click</a>` would render
 *    as a link inside a system notification; newlines let a name push the real text
 *    out of view or fake a second notice's worth of lines.
 * 2. **Bounded length.** Long titles are a wall of text on one platform and a
 *    silent drop on another.
 * 3. **A flood becomes one line.** `#p` is an open door — anyone can tag anyone —
 *    so a hostile actor can address a hundred events to a reader in a second.
 *    Beyond a handful, the notices collapse into a count.
 *
 * The Rust side applies rules 1 and 2 again before it calls the notifier. That is
 * not redundancy: this layer is the one an attacker who reaches the page is already
 * inside, so it cannot be the only layer that sanitises.
 */

import type { NotificationItem } from "../notifications/groupNotifications";
import { notificationLine } from "../notifications/notificationText";

/** Matches the ceiling in `notify.rs`, so a clamp here is never re-clamped there. */
const MAX_TITLE = 90;
const MAX_BODY = 220;

/** Names spelled out in the sentence, matching what a notification row does. */
const NAMED_LIMIT = 2;

/** Individual notices per delivery. Past this, one summary line. */
export const NOTICE_BURST_LIMIT = 3;

/**
 * C0 controls, DEL, and the three characters a markup parser reads.
 *
 * Replaced with a space rather than deleted: removing a newline would join the
 * words either side of it into one, which is a different sentence.
 */
// biome-ignore lint/suspicious/noControlCharactersInRegex: matching them is the point
const UNSAFE_IN_NOTICE = /[\u0000-\u001f\u007f<>&]/g;

export interface NoticeText {
  readonly title: string;
  readonly body: string;
}

/**
 * One line of plain text, at most `max` characters.
 *
 * Counts code points rather than slicing bytes, and trims before appending the
 * ellipsis so a cut landing on a space does not read as a gap the sender left.
 */
export function clampNoticeText(text: string, max: number): string {
  const collapsed = text
    .replace(UNSAFE_IN_NOTICE, " ")
    .replace(/\s+/g, " ")
    .trim();
  const points = [...collapsed];
  if (points.length <= max) return collapsed;
  return `${points.slice(0, max).join("").trimEnd()}…`;
}

/**
 * The notice for one grouped notification row.
 *
 * The title is the same sentence the in-app row shows, from the same function, so
 * the two can never describe the same event differently. The body is what was
 * written — the reply text, or the note that was acted on — and nothing is
 * substituted when there is neither: an empty body is a notification with a title,
 * which every platform renders correctly.
 */
export function noticeForItem(
  item: NotificationItem,
  names: readonly string[],
): NoticeText {
  const title = notificationLine({
    kind: item.kind,
    names: names.slice(0, NAMED_LIMIT),
    actorCount: item.actors.length,
    targetIsMine: item.targetIsMine,
    allLikes: item.allLikes,
  });

  const detail = item.bodyPreview ?? item.targetPreview ?? "";
  /*
   * A zap says who sent it on the authority of the recipient's LNURL server, not
   * of a signature this client checked — `groupNotifications.ts` calls that
   * `"claimed"` attribution, and the in-app row prints the caveat under the line.
   * A system notification has no room for a second paragraph, so the caveat goes
   * in the body. Dropping it because it is inconvenient would make the OS-level
   * surface the one place Setu asserts an identity it did not verify.
   */
  const body =
    item.kind === "zap"
      ? `Sender named by the receipt, not verified. ${detail}`
      : detail;

  return {
    title: clampNoticeText(title, MAX_TITLE),
    body: clampNoticeText(body, MAX_BODY),
  };
}

/** The one-line stand-in for a burst too large to show individually. */
export function summaryNotice(count: number): NoticeText {
  return {
    title: `${count} new notifications`,
    body: "Open Setu to see them.",
  };
}

/**
 * Split a batch into what gets its own notice and what gets counted.
 *
 * Newest first, because if only some of a batch is shown it should be the part
 * that just happened. The caller decides what "newest" means; this only preserves
 * the order it was given.
 */
export function planNotices<T>(
  pending: readonly T[],
  limit: number = NOTICE_BURST_LIMIT,
): { readonly show: readonly T[]; readonly summarised: number } {
  if (pending.length <= limit) return { show: pending, summarised: 0 };
  // Nothing individual when the batch is a flood: three notices *plus* "and 8
  // more" is four interruptions where the honest report is one.
  return { show: [], summarised: pending.length };
}
